import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import * as process from 'process';

import {
  SuspenseQueryOption,
  useSuspendedQuery,
  useZepApiClient,
  UseZepApiMutationOptions,
} from '@zep/apis';

import { businessQueryKeys } from '../consts';
import { BusinessPayment } from '../types/PaymentTypes';

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePayBusinessOrder = (
  businessPaymentHashId: string,
  options?: UseZepApiMutationOptions<{
    orderId: string;
    productType: 0 | 1;
    /**
     * 서버 VAT snapshot 금액 세트. PG amount는 finalPrice만 사용한다.
     * 서버 PR(VAT 정책) 합류 전에는 내려오지 않으므로 optional.
     * TODO: 서버 OpenAPI 완료 후 generated schema로 재생성
     */
    priceBeforeVat?: number;
    vatAmount?: number;
    vatRate?: number;
    finalPrice?: number;
    vatPolicyReason?: string;
  }>,
) => {
  const client = useZepApiClient();

  return useMutation(
    [businessQueryKeys.PAYMENT_ORDER_ID],
    async () => {
      const { data } = await client.get('/v2/business-payment/order', {
        params: {
          businessPaymentHashId,
        },
      });

      return data;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const usePaymentCustomerName = (
  businessPaymentHashId: string,
  options?: SuspenseQueryOption<string>,
) => {
  const client = useZepApiClient();
  return useSuspendedQuery(
    [businessQueryKeys.CHECK_PAYMENT_HASH_ID, businessPaymentHashId],
    async () => {
      const { data } = await client.get('/v2/business-payment/check-hash-id', {
        params: {
          businessPaymentHashId,
        },
      });
      return data.customerName;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useCheckPaymentPermission = (
  options?: UseMutationOptions<
    { status: number; businessPayment: BusinessPayment },
    AxiosError,
    { businessPaymentHashId: string; email: string }
  >,
) => {
  const client = useZepApiClient();

  return useMutation(
    [businessQueryKeys.CHECK_PAYMENT_PERMISSION],
    async ({ email, businessPaymentHashId }) => {
      const { data: checkPermission } = await client.get(
        '/v2/business-payment/check-permission',
        {
          params: {
            businessPaymentHashId,
            email,
          },
        },
      );
      return checkPermission;
    },
    options,
  );
};

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export const useBusinessPaymentResult = (
  { paymentHashId, email }: { paymentHashId: string; email: string },
  options?: SuspenseQueryOption<{
    status: number;
    businessPayment: BusinessPayment;
  }>,
) => {
  const client = useZepApiClient();
  return useSuspendedQuery(
    [businessQueryKeys.GET_PAYMENT_RESULT, paymentHashId, email],
    async () => {
      const { data } = await client.get('/v2/business-payment/result', {
        params: {
          businessPaymentHashId: paymentHashId,
          email,
        },
      });
      return data;
    },
    { ...options },
  );
};
export const BusinessPaymentTossSuccessCallbackUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/api/v2/business-payment/toss-callback/success`;
export const BusinessPaymentTossFailCallbackUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/api/v2/business-payment/toss-callback/fail`;

export const BusinessPaymentPaypleCallbackUrl =
  '/api/v2/business-payment/payple-callback';
